import { tokenStorage } from '../utils/tokenStorage';
import { refreshAccessToken } from './tokenRefresh';

const API_BASE_URL =
  import.meta.env.VITE_PLANNER_API_URL ||
  'http://localhost:3030' ||
  'https://planner-api-stage-01.hopsa.dev';

// Import generated types
export type ApiPaths = import('../generated/api-types').paths;

/**
 * Signal that a request definitively failed authentication after a refresh
 * attempt. The auth store listens for this to reconcile its state (clear the
 * stale user) so the UI stops showing a logged-in header with a dead session.
 */
function notifyUnauthorized(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('hopsa:unauthorized'));
  }
}

/**
 * Base API service class with common request handling and authentication
 */
export class BaseApiService {
  protected async makeRequest<T>(
    endpoint: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      params?: Record<string, string>;
      body?: any;
      requiresAuth?: boolean;
      allowAnonymousAuth?: boolean;
      skipAuthRefresh?: boolean;
      credentials?: RequestCredentials;
      signal?: AbortSignal;
    } = {},
  ): Promise<T> {
    const {
      method = 'GET',
      params,
      body,
      requiresAuth = true,
      allowAnonymousAuth = true,
      skipAuthRefresh = false,
      credentials,
      signal,
    } = options;
    const url = new URL(`${API_BASE_URL}${endpoint}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    const requestOptions: RequestInit = {
      method,
      headers: {} as Record<string, string>,
      signal, // Add abort signal support
    };

    if (credentials) {
      requestOptions.credentials = credentials;
    }

    // Only set Content-Type if we have a body
    if (body && method !== 'GET') {
      requestOptions.headers = {
        ...requestOptions.headers,
        'Content-Type': 'application/json',
      };
      requestOptions.body = JSON.stringify(body);
    }

    // Add authentication header if required and available
    if (requiresAuth) {
      const accessToken =
        tokenStorage.getAccessToken() ??
        (allowAnonymousAuth ? tokenStorage.getAnonymousToken() : null);
      if (accessToken) {
        requestOptions.headers = {
          ...requestOptions.headers,
          Authorization: `Bearer ${accessToken}`,
        };
      }
    }

    let response = await fetch(url.toString(), requestOptions);

    // Handle 401 Unauthorized responses with token refresh
    if (response.status === 401 && requiresAuth && !skipAuthRefresh) {
      // Try to refresh the token via the shared, deduplicated coordinator.
      const refreshResult = await refreshAccessToken();

      if (refreshResult.ok) {
        // Retry the request with the new token
        const newAccessToken = tokenStorage.getAccessToken();
        if (newAccessToken) {
          requestOptions.headers = {
            ...requestOptions.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };
          response = await fetch(url.toString(), requestOptions);

          // Refresh succeeded but the retry is still unauthorized — the session
          // is definitively invalid, so reconcile auth state.
          if (response.status === 401) {
            notifyUnauthorized();
            throw new Error('UNAUTHORIZED');
          }
        }
      } else {
        // Only tear down the session when the backend definitively rejected the
        // refresh token. Transient network errors leave state intact so a later
        // request can recover without logging the user out.
        if (refreshResult.authRejected) {
          notifyUnauthorized();
        }
        throw new Error('UNAUTHORIZED');
      }
    }

    if (!response.ok) {
      // For 401 errors that weren't handled above, throw UNAUTHORIZED
      if (response.status === 401) {
        throw new Error('UNAUTHORIZED');
      }

      // For 400, 409, 422 and 421 status codes, extract error message and data from response
      if (
        response.status === 400 ||
        response.status === 409 ||
        response.status === 422 ||
        response.status === 421
      ) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If JSON parsing fails, fall through to generic error
        }

        const errorMessage =
          errorData?.data?.error?.message ||
          errorData?.error?.message ||
          errorData?.message ||
          `API request failed: ${response.status} ${response.statusText}`;

        const error = new Error(errorMessage);
        (error as any).status = response.status;
        if (errorData) {
          (error as any).data = errorData;
        }
        throw error;
      }

      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`,
      );
    }

    // Handle 204 No Content - no body to parse
    if (response.status === 204) {
      return undefined as T;
    }

    const result = await response.json();

    return result.data;
  }

  /**
   * Get information about API configuration
   */
  getApiInfo() {
    return {
      hasGeneratedTypes: true,
      apiBaseUrl: API_BASE_URL,
      recommendation: 'Using generated types for better type safety ✅',
    };
  }
}
